/**
 * PreviewSize — screen metrics → the pixel size previews should be generated at.
 * Pure module: no imports, no I/O.
 *
 * A fixed 2048px preview is soft on a 4K panel and wasteful on a laptop. Sizing
 * to the actual display keeps perceived quality constant and storage proportional
 * to the hardware in front of the photographer.
 */

export const MIN_PREVIEW_PX = 1600;
export const MAX_PREVIEW_PX = 4096;
export const THUMB_PX = 240;

/** Headroom over the physical long edge so fullscreen and light zoom stay crisp. */
const HEADROOM = 1.5;

export function previewTargetPx(longEdgeCssPx: number, scaleFactor: number): number {
  const physical = Math.max(1, longEdgeCssPx) * Math.max(1, scaleFactor);
  const target = Math.round(physical * HEADROOM);
  return Math.min(MAX_PREVIEW_PX, Math.max(MIN_PREVIEW_PX, target));
}

/** Fit a source image inside a square of `longEdge`, never scaling up. */
export function fitWithin(
  width: number,
  height: number,
  longEdge: number,
): { width: number; height: number } {
  if (width <= 0 || height <= 0) return { width: longEdge, height: longEdge };

  const scale = Math.min(1, longEdge / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}
